import {Button, Modal, Tabs, TabsProps, Alert} from "antd";
import React, {useContext, useEffect, useState} from "react";
import {useParams} from "react-router-dom";
import {FormattedMessage, useIntl} from "react-intl";
import '../assets/css/Dentobook.css';
import {getMedicalStaffApi} from "../api/apiInstances";
import {MedicalStaff} from "../api-client";
import DetailWrapper from "../components/common/DetailWrapper";
import {accessibilityRoles} from "../utils/accessUtils";
import AccessControl from "../components/common/AccessControl";
import MedicalStaffDetailsItem from "./MedicalStaffDetailsItem";
import logger from "../utils/logger";
import {CloseOutlined, UserDeleteOutlined, UserAddOutlined} from "@ant-design/icons";
import {ALERT_DURATIONS} from "../constants/alerts";
import {DentobookApplicationContext} from "../context/DentobookApplicationContext";
import {AlertContext} from "../context/AlertContext";

const MedicalStaffDetails = () => {

    const {id} = useParams();
    const intl = useIntl();
    const {principal} = useContext(DentobookApplicationContext);
    const {setSuccessMessage, setVisible, setError, setWarning} = useContext(AlertContext);

    const [editMode, setEditMode] = useState<boolean>(false);
    const [medicalStaff, setMedicalStaff] = useState<MedicalStaff | undefined>(undefined);
    const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState<boolean>(false);
    const [isActioning, setIsActioning] = useState<boolean>(false);

    const medicalStaffApi = getMedicalStaffApi();
    const isClinicAdmin = principal?.user?.role === 'CLINIC_ADMIN';

    useEffect(() => {
        if (id == null) {
            return;
        }

        medicalStaffApi.fetchMedicalStaffById(id)
            .then((response) => {
                setMedicalStaff(response.data);
            })
            .catch(err => {
                logger.log(err);
            });
    }, [id, medicalStaffApi]);

    const handleDeactivateConfirm = async () => {
        if (!medicalStaff?.id) return;
        setIsActioning(true);
        try {
            const response = await medicalStaffApi.deactivateMedicalStaff(medicalStaff.id);
            setMedicalStaff(response.data);
            setSuccessMessage(intl.formatMessage({id: 'medical_staff_deactivated'}));
            setVisible(true);
            setTimeout(() => setVisible(false), ALERT_DURATIONS.STANDARD);
        } catch (error: any) {
            if (error.response?.status === 409) {
                setWarning(intl.formatMessage({id: 'medical_staff_already_deactivated'}));
            } else {
                setError(intl.formatMessage({id: 'unexpected_error'}));
            }
        } finally {
            setIsActioning(false);
            setIsDeactivateModalOpen(false);
        }
    };

    const handleActivate = async () => {
        if (!medicalStaff?.id) return;
        setIsActioning(true);
        try {
            const response = await medicalStaffApi.activateMedicalStaff(medicalStaff.id);
            setMedicalStaff(response.data);
            setSuccessMessage(intl.formatMessage({id: 'medical_staff_activated'}));
            setVisible(true);
            setTimeout(() => setVisible(false), ALERT_DURATIONS.STANDARD);
        } catch (error: any) {
            if (error.response?.status === 409) {
                setWarning(intl.formatMessage({id: 'medical_staff_already_active'}));
            } else {
                setError(intl.formatMessage({id: 'unexpected_error'}));
            }
        } finally {
            setIsActioning(false);
        }
    };

    // Guard: medicalStaff must be loaded before rendering tabs
    if (!medicalStaff) {
        return null;
    }

    const deactivateModalFooter = (
        <div className="modal-btn-wrapper">
            <Button className="modal-btn" onClick={() => setIsDeactivateModalOpen(false)}>
                <FormattedMessage id="cancel"/>
            </Button>
            <Button className="modal-btn" type="primary" danger loading={isActioning}
                    onClick={handleDeactivateConfirm}>
                <FormattedMessage id="confirm"/>
            </Button>
        </div>
    );

    const items: TabsProps['items'] = [
        {
            label: <FormattedMessage id="main_information"/>,
            key: 'main-information',
            children: <MedicalStaffDetailsItem medicalStaff={medicalStaff}
                                               setMedicalStaff={setMedicalStaff}
                                               editMode={editMode}
                                               setEditMode={setEditMode}/>
        },
        {
            label: <FormattedMessage id="finances"/>,
            key: 'finances',
            children: <Alert message={<FormattedMessage id="upcoming_feature" />} type="info" showIcon />
        },
        {
            label: <FormattedMessage id="work_time"/>,
            key: 'work_time',
            children: <Alert message={<FormattedMessage id="upcoming_feature" />} type="info" showIcon />
        }
    ];

    return (
        <AccessControl
            accessibleFor={[accessibilityRoles.CLINIC_ADMIN, accessibilityRoles.RECEPTIONIST]}>
            <Modal
                transitionName=""
                footer={deactivateModalFooter}
                className="dentobook-modal"
                title={<FormattedMessage id="deactivate_medical_staff"/>}
                closeIcon={<CloseOutlined className="icon-24px" onClick={() => setIsDeactivateModalOpen(false)}/>}
                open={isDeactivateModalOpen}
                onOk={handleDeactivateConfirm}
                onCancel={() => setIsDeactivateModalOpen(false)}>
                <h3 className="dentobook-content font-weight-500 font-24 text-align-center">
                    <FormattedMessage id="confirmation_text"/>
                </h3>
            </Modal>
            <DetailWrapper navigateBack="/medical/staff">
                {isClinicAdmin && (
                    <div className="top-right-btns" style={{marginTop: -64}}>
                        {medicalStaff.isActive ? (
                            <Button className="text-black font-20"
                                    icon={<UserDeleteOutlined style={{fontSize: '140%'}}/>}
                                    size="large" type="link"
                                    onClick={() => setIsDeactivateModalOpen(true)}/>
                        ) : (
                            <Button className="text-black font-20"
                                    icon={<UserAddOutlined style={{fontSize: '140%'}}/>}
                                    size="large" type="link"
                                    loading={isActioning}
                                    onClick={handleActivate}/>
                        )}
                    </div>
                )}
                <h3>{medicalStaff.firstName + ' ' + medicalStaff.lastName}</h3>
                <Tabs defaultActiveKey="main-information" items={items}/>
            </DetailWrapper>
        </AccessControl>
    );
};

export default MedicalStaffDetails;
